import PropTypes from 'prop-types';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { batch } from 'react-redux';

// material-ui
import { TextField, Box, Stack, Typography } from '@mui/material';
import Icon from '@mui/material/Icon';

// third party
import { useIntl } from 'react-intl';

// project imports
import { useSelector, useDispatch } from 'store';
import { openSnackbar } from 'store/slices/snackbar';
import { ApiClientsGetCollection } from 'store/app/crm/client';
import Autocomplete from 'ui-component/extended/Autocomplete';

// ==============================|| SELECT CLIENT FIELD ||============================== //

export const renderClient = (client) => {
    if (!client) return '';
    if (typeof client === 'string') return client;
    return client?.name || '';
};

const ClientSelect = ({
    value,
    onChange = () => {},
    validationErrors = null,
    baseId = '',
    enableSearch = true,
    loadData = false,
    baseFilterGroups = [],
    ...others
}) => {
    const dispatch = useDispatch();
    const intl = useIntl();

    const { list: clientList, quickSearchFilterDef: clientQuickSearchFilterDef } = useSelector((state) => state.client);

    const [isLoadingClient, setIsLoadingClient] = useState(false);
    const [userInputClient, setUserInputClient] = useState('');
    const [clients, setClients] = useState(clientList || []);

    const clientFilterRows = useMemo(
        () =>
            clientQuickSearchFilterDef?.fields?.map((fd) => ({
                field: fd,
                operator: 'like',
                value: userInputClient
            })),
        [clientQuickSearchFilterDef, userInputClient]
    );

    const handleLoadClients = useCallback(() => {
        setIsLoadingClient(true);
        ApiClientsGetCollection({
            filters: baseFilterGroups,
            successCallback: (response) =>
                batch(() => {
                    setIsLoadingClient(false);
                    setClients(response);
                    setUserInputClient('');
                }),
            errorCallback: (error) =>
                dispatch(
                    openSnackbar({
                        open: true,
                        message: error || intl.formatMessage({ id: 'ui-comp-snackbar-genericerror-text' }),
                        variant: 'alert',
                        alert: { color: 'error' }
                    })
                ),
            updateStore: false
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [baseFilterGroups]);

    useEffect(() => {
        if (loadData) {
            handleLoadClients();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadData]);

    useEffect(() => {
        if (value === null) {
            setUserInputClient('');
            handleLoadClients();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    useEffect(() => {
        if (enableSearch && userInputClient?.length >= 3) {
            setIsLoadingClient(true);
            ApiClientsGetCollection({
                filters: [...baseFilterGroups, { filterType: 'or', filterRows: clientFilterRows }],
                successCallback: (response) =>
                    batch(() => {
                        setIsLoadingClient(false);
                        setClients(response);
                    }),
                errorCallback: (error) =>
                    batch(() => {
                        setIsLoadingClient(false);
                        dispatch(
                            openSnackbar({
                                open: true,
                                message: error || intl.formatMessage({ id: 'ui-comp-snackbar-genericerror-text' }),
                                variant: 'alert',
                                alert: { color: 'error' }
                            })
                        );
                    }),
                updateStore: false
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enableSearch, clientFilterRows]);

    return (
        <Autocomplete
            id={`${baseId}-client`}
            value={value}
            onChange={(value) =>
                batch(() => {
                    onChange(value);
                    setUserInputClient(renderClient(value));
                })
            }
            inputValue={userInputClient || renderClient(value)}
            onInputChange={(value, reason) => {
                if (reason === 'input') {
                    setUserInputClient(value);
                }
                if (reason === 'clear') {
                    setUserInputClient('');
                    handleLoadClients();
                }
            }}
            loading={isLoadingClient}
            options={clients}
            freeSolo={enableSearch}
            getOptionLabel={(option) => renderClient(option)}
            renderOption={(props, option) => (
                <Box component="li" {...props}>
                    <Stack direction="row" spacing={2} alignItems="center">
                        {option?.partnershipType && (
                            <Icon
                                sx={{
                                    fontSize: '1.5rem !important',
                                    color: `rgba(${option?.partnershipType?.color?.r}, ${option?.partnershipType?.color?.g}, ${option?.partnershipType?.color?.b}, ${option?.partnershipType?.color?.a})`
                                }}
                            >
                                {option?.partnershipType?.icon}
                            </Icon>
                        )}
                        <Typography variant="inherit" color="inherit">
                            {renderClient(option)}
                        </Typography>
                    </Stack>
                </Box>
            )}
            renderInput={(params) => {
                const { multiple } = others;

                if (!multiple && value?.partnershipType) {
                    return (
                        <TextField
                            {...params}
                            InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                    <Icon
                                        sx={{
                                            fontSize: '1.5rem !important',
                                            color: `rgba(${value?.partnershipType?.color?.r}, ${value?.partnershipType?.color?.g}, ${value?.partnershipType?.color?.b}, ${value?.partnershipType?.color?.a})`
                                        }}
                                    >
                                        {value?.partnershipType?.icon}
                                    </Icon>
                                )
                            }}
                            error={Boolean(validationErrors)}
                            helperText={validationErrors}
                        />
                    );
                }

                return (
                    <TextField
                        {...params}
                        inputProps={{ ...params.inputProps }}
                        error={Boolean(validationErrors)}
                        helperText={validationErrors}
                    />
                );
            }}
            {...others}
        />
    );
};

ClientSelect.propTypes = {
    value: PropTypes.oneOfType([PropTypes.object, PropTypes.arrayOf(PropTypes.object)]),
    onChange: PropTypes.func,
    validationErrors: PropTypes.any,
    baseId: PropTypes.string,
    enableSearch: PropTypes.bool,
    loadData: PropTypes.bool,
    baseFilterGroups: PropTypes.array
};

export default ClientSelect;
